import { AppName } from "./data";
import { AssetsResponse, SiteLandingPageLocaleProps } from "./responses";

export const getSiteName = (assets?: AssetsResponse | null) =>
  assets?.branding?.siteName || AppName;

export const getSiteLogo = (assets?: AssetsResponse | null) =>
  assets?.branding?.primaryLogo || assets?.logos?.legacy || "";

export const buildFallbackLandingPageContent = (
  t: any
): SiteLandingPageLocaleProps => ({
  hero: {
    isEnabled: true,
    badgeText: t("home.hero.trustedBy"),
    title: t("home.hero.title"),
    highlightedTitle: t("home.hero.liveLearning"),
    description: t("home.hero.description"),
    ctaLabel: t("home.hero.startLearningNow"),
    heroImage: "",
  },
  stats: {
    isEnabled: true,
    items: [
      { label: t("home.stats.activeStudents"), value: "50,000+" },
      { label: t("home.stats.expertInstructors"), value: "200+" },
      { label: t("home.stats.liveSessions"), value: "1000+" },
      { label: t("home.stats.successRate"), value: "95%" },
    ],
  },
  features: {
    isEnabled: true,
    title: t("home.features.title"),
    subtitle: t("home.features.description"),
    items: [
      {
        title: t("home.features.liveSessions"),
        description: t("home.features.liveSessionsDesc"),
      },
      {
        title: t("home.features.comprehensiveMaterials"),
        description: t("home.features.comprehensiveMaterialsDesc"),
      },
      {
        title: t("home.features.communityLearning"),
        description: t("home.features.communityLearningDesc"),
      },
      {
        title: t("home.features.flexibleScheduling"),
        description: t("home.features.flexibleSchedulingDesc"),
      },
    ],
  },
  promo: {
    isEnabled: false,
    title: t("home.promo.saveAmount"),
    body: `${t("home.promo.enrollmentMessage")} ${t("home.promo.enrollmentPeriod")}`,
    ctaLabel: t("home.hero.getStarted"),
    priceText: t("home.promo.monthlyPrice"),
    image: "",
  },
  about: {
    isEnabled: true,
    title: t("home.about.title"),
    subtitle: t("home.about.subtitle"),
    visionTitle: t("home.about.visionTitle"),
    vision: t("home.about.vision"),
    missionTitle: t("home.about.missionTitle"),
    mission: t("home.about.mission"),
    historyTitle: t("home.about.historyTitle"),
    history: t("home.about.history"),
    valuesTitle: t("home.about.valuesTitle"),
    values: t("home.about.values"),
  },
  howItWorks: {
    isEnabled: true,
    title: t("home.howItWorks.title"),
    subtitle: t("home.howItWorks.subtitle"),
    steps: [
      {
        title: t("home.howItWorks.step1"),
        description: t("home.howItWorks.step1Desc"),
      },
      {
        title: t("home.howItWorks.step2"),
        description: t("home.howItWorks.step2Desc"),
      },
      {
        title: t("home.howItWorks.step3"),
        description: t("home.howItWorks.step3Desc"),
      },
    ],
  },
  testimonials: {
    isEnabled: false,
    title: t("home.testimonials.title"),
    subtitle: t("home.testimonials.subtitle"),
    items: [
      {
        name: "Sarah Johnson",
        role: "Medical Student",
        avatar:
          "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
        text: t("home.testimonials.medicalStudent"),
      },
      {
        name: "Michael Chen",
        role: "Engineering Student",
        avatar:
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        text: t("home.testimonials.engineeringStudent"),
      },
      {
        name: "Emily Rodriguez",
        role: "Law Student",
        avatar:
          "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
        text: t("home.testimonials.lawStudent"),
      },
    ],
  },
  teachers: {
    isEnabled: false,
    title: t("home.teachers.title"),
    subtitle: t("home.teachers.subtitle"),
    items: [
      {
        name: "Dr. Sarah Johnson",
        avatar:
          "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
        title: t("home.profiles.1.title"),
        description: t("home.profiles.1.description"),
      },
      {
        name: "Prof. Michael Chen",
        avatar:
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        title: t("home.profiles.2.title"),
        description: t("home.profiles.2.description"),
      },
      {
        name: "Attorney Emily Rodriguez",
        avatar:
          "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
        title: t("home.profiles.3.title"),
        description: t("home.profiles.3.description"),
      },
      {
        name: "Sarah Williams",
        avatar:
          "https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=150&h=150&fit=crop&crop=face",
        title: t("home.profiles.4.title"),
        description: t("home.profiles.4.description"),
      },
    ],
  },
  coursesOffered: {
    isEnabled: true,
    title: t("home.coursesOffered.title"),
    subtitle: t("home.coursesOffered.subtitle"),
    items: [
      {
        title: t("home.coursesOffered.maths.title"),
        description: t("home.coursesOffered.maths.description"),
      },
      {
        title: t("home.coursesOffered.physics.title"),
        description: t("home.coursesOffered.physics.description"),
      },
      {
        title: t("home.coursesOffered.english.title"),
        description: t("home.coursesOffered.english.description"),
      },
      {
        title: t("home.coursesOffered.chemistry.title"),
        description: t("home.coursesOffered.chemistry.description"),
      },
      {
        title: t("home.coursesOffered.biology.title"),
        description: t("home.coursesOffered.biology.description"),
      },
    ],
  },
  footer: {
    isEnabled: true,
    tagline: t("footer.tagline"),
    phone: "+44 7723 864092",
    email: "info@emrilacademy.com",
    nigeriaLocation: "10 Adeyemi Odukomaya, Magodo, Lagos",
    ukLocation: "207 Blackthorn Rd, Ilford IG1 2PZ",
    companyLinks: [
      { label: t("footer.aboutUs"), href: "/about" },
      { label: t("footer.careers"), href: "/careers" },
      { label: t("navigation.events"), href: "/events" },
      { label: t("navigation.blog"), href: "/blogs" },
    ],
    learningLinks: [
      { label: t("auth.signUp"), href: "/sign-up" },
      { label: t("navigation.plans"), href: "/plans" },
      { label: t("footerExtras.studentPortal"), href: "/login" },
    ],
    supportLinks: [
      { label: t("footer.termsOfService"), href: "/terms" },
      { label: t("footer.privacyPolicy"), href: "/privacy" },
      { label: "Join Waitlist", href: "/join" },
    ],
    socialLinks: [],
  },
});

export const getLocalizedLandingPageContent = (
  assets: AssetsResponse | null | undefined,
  language: string | undefined,
  t: any
) => {
  const fallback = buildFallbackLandingPageContent(t);
  const englishSource = assets?.landingPage?.en;
  const localeKey = (language || "en").split("-")[0];
  const source = englishSource || assets?.landingPage?.[localeKey];

  if (!source) {
    return fallback;
  }

  return {
    ...fallback,
    ...source,
    hero: { ...fallback.hero, ...source.hero },
    stats: { ...fallback.stats, ...source.stats },
    features: { ...fallback.features, ...source.features },
    promo: { ...fallback.promo, ...source.promo },
    about: { ...fallback.about, ...source.about },
    howItWorks: { ...fallback.howItWorks, ...source.howItWorks },
    testimonials: { ...fallback.testimonials, ...source.testimonials },
    teachers: { ...fallback.teachers, ...source.teachers },
    coursesOffered: { ...fallback.coursesOffered, ...source.coursesOffered },
    footer: { ...fallback.footer, ...source.footer },
  };
};
